import type { PostListItem } from '@/entities/post/post-types';
import type {
  AGE,
  CONTENT_TYPE,
  GENDER,
  RACE,
  SOURCE_PLATFORM_TYPE,
} from '@/entities/public-filter/public-filter-constants';
import type { QueryDate } from '@/entities/query/query-types';
import { socialApiClient } from '@/shared/api-client/social-api-client';
import type { PublicFilter } from '../../../features/(insight)/shared/public-filter/use-public-filter';
import { buildPublicFilterWithDates } from '../../insight/insight-utils';
import type { PostFilterMap } from '../models';

export interface PostsParams {
  queryId: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  sortBy?: SortOption;
  privateFilter?: PostFilterMap;
  publicFilter: PublicFilter;
  searchAfter?: string;
}

export type PublicFilterWithDates = {
  keyword: string;
  sponsored: CONTENT_TYPE[];
  platform: SOURCE_PLATFORM_TYPE[];
  start_date?: QueryDate;
  end_date?: QueryDate;
  gender?: GENDER[];
  race?: RACE[];
  age?: AGE[];
  location?: string[];
};

export interface PostsRequestBody {
  filter: {
    private: PostFilterMap;
    public: PublicFilterWithDates;
  };
  sortby?: SortOption;
  limit?: number;
  search_after?: string;
}

export interface PostsPage {
  posts: PostListItem[];
  next_token?: string;
  total: number;
}

export type SortOption = 'view' | 'date' | 'score' | 'like' | 'comment' | 'shared' | 'er';

export const SORT_OPTION_MAP = new Map<SortOption, string>([
  ['score', 'Most relevant'],
  ['date', 'Most recent'],
  ['view', 'Views'],
  ['like', 'Likes'],
  ['comment', 'Comments'],
  ['shared', 'Shares'],
  ['er', 'Engagement Rate'],
]);

export const SORT_OPTION_LIST = Array.from(SORT_OPTION_MAP.entries()).map(([value, label]) => ({
  value,
  label,
}));

export const SORT_OPTION_LIST_WITHOUT_SCORE = SORT_OPTION_LIST.filter(
  ({ value }) => value !== 'score',
).map(({ value, label }) => ({
  value,
  label,
}));

// Private fetch function - not exported
export async function fetchPosts(params: PostsParams, signal?: AbortSignal): Promise<PostsPage> {
  const requestBody = convertPostsRequestBody(params);
  const response = await socialApiClient
    .post(`queries/${params.queryId}/posts`, {
      json: requestBody,
      signal,
    })
    .json<{
      next_token: string;
      total: number;
      posts: Array<PostListItem>;
    }>();

  return response;
}

const ITEMS_PER_PAGE = 20;

export const convertPostsRequestBody = (params: PostsParams): PostsRequestBody => {
  const { startDate, endDate, limit, sortBy, privateFilter, publicFilter } = params;

  const publicFilterBody: PublicFilterWithDates = buildPublicFilterWithDates(
    publicFilter,
    startDate,
    endDate,
  );

  const requestBody: PostsRequestBody = {
    limit: limit ?? ITEMS_PER_PAGE,
    filter: {
      private: privateFilter ?? {},
      public: publicFilterBody,
    },
    sortby: sortBy,
    search_after: params.searchAfter,
  };

  return requestBody;
};
